import {
  useFormatCurrency,
  useQuantityFormat,
  useTranslation,
} from '@aboutyou/i18n';
import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {PriceFilterRange} from '../../../../../types/PriceFilter';
import {Checkbox} from '../../../../atoms/Checkbox/Checkbox';
import {FilterCard} from '../../../../atoms/FilterCard/FilterCard';
import {NumberBadge} from '../../../../atoms/NumberBadge/NumberBadge';
import {PriceRangeInput} from '../../FilterContent/Price/PriceRangeInput';
import {PriceRangeManualInput} from '../../FilterContent/Price/PriceRangeManualInput';
import {FilterFlyout, Sections} from '../FilterFlyout';
import {FilterFlyoutFooter} from '../FilterFlyoutFooter';

interface Props {
  label: string;
  max: number;
  min: number;
  currencyCode: string;
  onApply: (range: [number, number]) => void;
  onChange: (range: [number, number]) => void;
  values: [number, number];
  isOpen: boolean;
  open: VoidFunction;
  hide: VoidFunction;
  submit: VoidFunction;
  hasActivePrice?: boolean;
  priceRanges: PriceFilterRange[];
}

const FlyoutFilter = (props: PropsWithChildren<Props>) => {
  const {
    currencyCode,
    open,
    hasActivePrice,
    values,
    hide,
    isOpen,
    label,
    min,
    max,
    onApply,
    onChange,
    children,
    submit,
  } = props;
  const {t} = useTranslation();
  const quantityFormat = useQuantityFormat();
  const formatCurrency = useFormatCurrency();
  const formatValue = (value: number) =>
    formatCurrency(value, currencyCode, {
      withoutDecimal: true,
    });

  return (
    <FilterCard
      onClick={open}
      title={t('Price')}
      disableHorizontalPadding={true}
      count={
        hasActivePrice
          ? `${formatValue(values[0])} - ${formatValue(values[1])}`
          : undefined
      }
    >
      <StyledFilterFlyout
        data-testid="filterFlyoutSectionPrice_filterFlyout"
        onClose={hide}
        open={isOpen}
        title={t('Price')}
        primaryButtonLabel={t('Submit')}
        secondaryButtonLabel={t('Back')}
        footer={FilterFlyoutFooter}
        onPrimaryButtonClick={submit}
      >
        <FlyoutWrapper>
          <PaddingWrapper>
            <StyledPriceRangeManualInput
              label={label}
              min={min}
              max={max}
              currencyCode={currencyCode}
              values={values}
              onApply={onApply}
              onChange={onChange}
            />
            <StyledPriceRangeInput
              max={max}
              min={min}
              currencyCode={currencyCode}
              values={values}
              onApply={onApply}
              onChange={onChange}
            />
            {children}
          </PaddingWrapper>

          <RangeWrapper>
            {props.priceRanges.map(range => {
              const selected =
                values[0] === range.min && values[1] === range.max;
              return (
                <RangeWrapperItem
                  key={`${range.label}-${selected}`}
                  data-selected={selected}
                  onClick={() =>
                    onChange(selected ? [min, max] : [range.min, range.max])
                  }
                >
                  <Checkbox
                    checked={selected}
                    label={range.label}
                    fullLabelWidth={true}
                    onChange={() =>
                      onChange(selected ? [min, max] : [range.min, range.max])
                    }
                  />
                  <StyledNumberBadge
                    count={quantityFormat(range.productCount)}
                    size="small"
                  />
                </RangeWrapperItem>
              );
            })}
          </RangeWrapper>
        </FlyoutWrapper>
      </StyledFilterFlyout>
    </FilterCard>
  );
};

export const FilterFlyoutPrice: FC<React.PropsWithChildren<Props>> = props => {
  return (
    <div data-testid="filterFlyoutSectionPrice">
      <FlyoutFilter {...props} />
    </div>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FlyoutWrapper = styled(Wrapper)`
  height: 100%;
`;

const PaddingWrapper = styled.div`
  padding: 0 1rem;
  padding-top: 1rem;
`;

const StyledPriceRangeManualInput = styled(PriceRangeManualInput)`
  margin-bottom: 12px;
`;

const StyledPriceRangeInput = styled(PriceRangeInput)`
  margin: 0 12px;
`;

const RangeWrapper = styled.div`
  width: 100%;
`;

const RangeWrapperItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1em 1rem;
  &[data-selected='true'] {
    background-color: ${COLORS['Fill.Grey.Primary']};
  }
  &:first-child {
    margin-top: 4px; // its added so that sale filter is not overlapping with padding when item selected
  }
`;

const StyledFilterFlyout = styled(FilterFlyout)`
  ${Sections} {
    padding: 0;
  }
`;

const StyledNumberBadge = styled(NumberBadge)`
  border: 1px solid ${COLORS['Outline.Grey']};
`;
